import { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Divider } from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiAutocomplete from "components/AutoComplete";
import OverlayLoader from "components/OverlayLoader";

import useCommon from "hooks/useCommon";
import { getUsers } from "store/slice/userSlice";
import { getInHouseGuests } from "store/slice/guestSlice";
import { FaExclamationCircle } from "react-icons/fa";
import { resetCreateRequestState } from "store/slice/requestSlice";

const CreateRequestDialog = ({ open, onClose, onCreate }) => {
  const { STORE, DISPATCH } = useCommon();

  const { guests, listLoading: guestsLoading } = STORE((state) => state.guests);

  const { list: users, listLoading: usersLoading } = STORE((state) => state.user);
  const { createRequestLoading, createRequestError } = STORE((state) => state.requests);

  const hotelId = STORE((state) => state.ui.hotelId);

  const [form, setForm] = useState({
    guestId: "",
    assignedTo: [],
    title: "",
    description: "",
    dueDate: "",
    dueTime: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      DISPATCH(resetCreateRequestState());
      DISPATCH(getUsers({ hotelId, isOnlyActive: true, includeMe: true, includeAdmin: true }));

      if (!guests || guests.length === 0) {
        DISPATCH(getInHouseGuests({ hotelId }));
      }
    }
  }, [open, hotelId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.guestId) newErrors.guestId = "Guest is required";
    if (!form.title) newErrors.title = "Title is required";
    if (!form.description) newErrors.description = "Description is required";

    const selectedGuest = guests.find((g) => g.guestId === form.guestId);

    if (form.dueDate && selectedGuest) {
      const dueDateTime = new Date(`${form.dueDate}T${form.dueTime || "00:00"}`);

      // Arrival validation
      if (selectedGuest?.arrivedAt) {
        const arrivedAt = new Date(selectedGuest.arrivedAt);

        if (dueDateTime < arrivedAt) {
          newErrors.dueDate = "Due date cannot be before guest arrival time";
        }
      }

      // Checkout validation
      if (selectedGuest?.checkOutDate) {
        const checkOutDate = new Date(selectedGuest.checkOutDate);

        if (dueDateTime > checkOutDate) {
          newErrors.dueDate = "Due date cannot be after guest checkout date";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const selectedGuest = guests.find((g) => g.guestId === form.guestId);

    const payload = {
      hotelId,
      guestId: form.guestId,
      reservationId: selectedGuest?.reservationId,
      title: form.title,
      description: form.description,
      assignedToUserIds: form.assignedTo,
      dueDate: form.dueDate
        ? new Date(`${form.dueDate}T${form.dueTime || "00:00"}`).toISOString()
        : null,
      dueTime: form.dueTime || null,
    };

    onCreate?.(payload);
  };

  useEffect(() => {
    if (!open) {
      setForm({
        guestId: "",
        assignedTo: [],
        title: "",
        description: "",
        dueDate: "",
        dueTime: "",
      });

      setErrors({});
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      {createRequestLoading && (
        <OverlayLoader open={createRequestLoading} message="Creating request..." />
      )}

      <DialogTitle>
        <VuiTypography variant="h5">Create Request</VuiTypography>
      </DialogTitle>

      <DialogContent>
        <VuiBox display="flex" flexDirection="column" gap={0.5} mt={0}>
          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Guest*
            </VuiTypography>

            <VuiAutocomplete
              options={guests || []}
              value={guests.find((g) => g.guestId === form.guestId) || null}
              getOptionLabel={(option) =>
                `${option.guestName || ""}${option.roomName ? ` • ${option.roomName}` : ""}`
              }
              renderOption={(props, option) => (
                <li {...props}>
                  <VuiBox display="flex" flexDirection="column">
                    <VuiTypography variant="caption" color="text" mb={0.4}>
                      {option.guestName}
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text">
                      {option.roomNumber} {option.occasion ? `• ${option.occasion}` : ""}
                    </VuiTypography>
                  </VuiBox>
                </li>
              )}
              isOptionEqualToValue={(opt, val) => opt.guestId === val.guestId}
              placeholder="Select guest"
              gradient
              loading={guestsLoading}
              onChange={(_, selected) => {
                handleChange("guestId", selected ? selected.guestId : "");
              }}
            />

            {errors.guestId && (
              <VuiTypography variant="caption" color="error" ml={1}>
                {errors.guestId}
              </VuiTypography>
            )}
          </VuiBox>

          {/* ================= TITLE ================= */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Request Title*
            </VuiTypography>

            <VuiInput
              gradient
              fullWidth
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              error={!!errors.title}
            />

            {errors.title && (
              <VuiTypography variant="caption" color="error">
                {errors.title}
              </VuiTypography>
            )}
          </VuiBox>

          {/* ================= DESCRIPTION ================= */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Description*
            </VuiTypography>

            <VuiInput
              gradient
              fullWidth
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              error={!!errors.description}
              inputProps={{ maxLength: 500 }}
            />

            {errors.description && (
              <VuiTypography variant="caption" color="error">
                {errors.description}
              </VuiTypography>
            )}
          </VuiBox>

          {/* ================= ASSIGN MULTIPLE USERS ================= */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1}>
              Assign To
            </VuiTypography>

            <VuiAutocomplete
              multiple
              options={users || []}
              value={users?.filter((u) => form.assignedTo.includes(u._id))}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              placeholder="Assign users"
              gradient
              loading={usersLoading}
              onChange={(_, selected) => {
                handleChange(
                  "assignedTo",
                  selected.map((u) => u._id)
                );
              }}
            />
          </VuiBox>

          {/* ================= DUE DATE + TIME ================= */}
          <VuiBox>
            <VuiTypography variant="caption" ml={1} mb={0.5}>
              Due Date & Time
            </VuiTypography>

            <VuiBox display="flex" gap={1}>
              <VuiInput
                type="date"
                gradient
                fullWidth
                value={form.dueDate}
                onChange={(e) => handleChange("dueDate", e.target.value)}
              />

              <VuiInput
                type="time"
                gradient
                fullWidth
                value={form.dueTime}
                onChange={(e) => handleChange("dueTime", e.target.value)}
              />
            </VuiBox>

            {errors.dueDate && (
              <VuiTypography variant="caption" color="error" mt={0.5}>
                {errors.dueDate}
              </VuiTypography>
            )}
          </VuiBox>
        </VuiBox>

        {createRequestError && (
          <VuiBox
            display="flex"
            alignItems="center"
            gap={1}
            p={1.2}
            mb={1.5}
            mt={2}
            borderRadius="md"
            sx={{
              border: "1px solid rgba(239, 68, 68, 0.4)",
              background: "rgba(239, 68, 68, 0.08)",
            }}
          >
            <FaExclamationCircle size={14} color="#ef4444" />

            <VuiTypography variant="caption" color="error">
              {createRequestError}
            </VuiTypography>
          </VuiBox>
        )}
      </DialogContent>

      <DialogActions>
        <VuiButton variant="outlined" color="primary" onClick={onClose}>
          Cancel
        </VuiButton>

        <VuiButton
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={createRequestLoading}
        >
          {createRequestLoading ? "Creating..." : "Create Request"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRequestDialog;
